/**
 * Interfaces
 */
import { IUserInfoDto } from "../../interfaces";
import { IUserInfoModel, IUserInfoModelControl } from "./interfaces";
import { UserBaseModel } from "../base-user-model";

/**
 * Represents user information in the application.
 * 
 * This model provides a clean interface to work with user data. It extends
 * the base user model and adds user-specific properties like ID and role name.
 * All properties use camelCase naming which is standard in JavaScript.
 * 
 * The model wraps the DTO internally and provides getters and setters that
 * convert between the API's snake_case format and the application's camelCase format.
 */
export class UserInfoModel extends UserBaseModel<IUserInfoDto> implements IUserInfoModelControl {

    /**
     * Sets the unique identifier for this user.
     * 
     * @param value - The user ID as a string
     * @throws Error if the value is not a string
     */
    public set id(value: string) {
        if (typeof value !== 'string') {
            throw new Error('ID must be a string');
        }
        this._userInfo.id = value;
    }

    /**
     * Gets the unique identifier for this user.
     * 
     * @returns The user ID
     */
    public get id(): string {
        return this._userInfo.id;
    }

    /**
     * Gets the user's full name by combining first and last name.
     * 
     * This is a computed property that automatically combines firstName
     * and lastName with a space between them.
     * 
     * @returns The user's full name
     */
    public get fullName(): string {
        return `${this.firstName} ${this.lastName}`;
    }

    /**
     * Sets the user's role name.
     * 
     * The role name is a human-readable description like "Administrator"
     * or "Manager" that describes what the user can do in the system.
     * 
     * @param value - The role name as a string
     * @throws Error if the value is not a string
     */
    public set roleName(value: string) {
        if (typeof value !== 'string') {
            throw new Error('Role name must be a string');
        }
        this._userInfo.role_name = value;
    }

    /**
     * Gets the user's role name.
     * 
     * @returns The role name
     */
    public get roleName(): string {
        return this._userInfo.role_name;
    }
    
    /**
     * Gets the multi-factor authentication factor ID if MFA is enabled.
     * 
     * If the user has multi-factor authentication set up, this will contain
     * the ID of the MFA factor. Otherwise it will be null.
     * 
     * @returns The MFA factor ID or null if MFA is not enabled
     */
    public get mfaFactorId(): string | null {
        return this._userInfo.mfa_factor_id;
    }

    /**
     * Gets whether the user has multi-factor authentication enabled.
     * This is a computed property that checks if the mfaFactorId is not null.
     * @returns True if MFA is enabled, false otherwise
     */
    public get isMfaEnabled(): boolean {
        return this.mfaFactorId != null;
    }

    /**
     * Creates a new user information model instance.
     * 
     * @param _userInfo - The user information data transfer object from the API
     */
    constructor(protected _userInfo: IUserInfoDto) {
        super(_userInfo);
    }

}